import {
	useEffect,
	useState,
	useRef,
	useContext,
} from 'react';
import { useDispatch } from 'react-redux';
import { DeviceTypes, GreenroomState as GRS } from '../stores/types/types';
import { RESTRICTED_USER_STATES } from '../stores/reducers/session';
import { ReportError } from '../connection/errors';
import * as VideoActions from '../stores/actions/video';
import * as SessionActions from '../stores/actions/session';
import * as VideoStreams from '../utils/video/video-streams';
import { hideDeviceAlert, showAlertLong } from '../components/general-components/alert-service';
import store from '../stores/main';
import { OpenTokContext } from '../contexts/opentok/opentok-context';
import useUpdateDevices from './devices/use-update-devices';

export interface mediaErrorMessage {
	message: string;
	description: string;
}

export type MediaCheckResult =
	| 'ok'
	| 'getUserMedia_unavailable'
	| 'permissions_off'
	| `error:${string}`;

const useMediaDevices = () => {
	const dispatch = useDispatch();

	const { opentok, changeAudioDevice, changeVideoDevice } = useContext(OpenTokContext);

	const { updateMediaDevices } = useUpdateDevices();

	const [error, setError] = useState<mediaErrorMessage>({ message: '', description: '' });

	const deviceRef = useRef({
		cameras: [] as MediaDeviceInfo[],
		microphones: [] as MediaDeviceInfo[],
		speakers: [] as MediaDeviceInfo[],
	});

	const getError = (device: number, permission: boolean) => {
		dispatch(SessionActions.setRestrictedUserState(device));

		let deviceString: string = '';
		if (device === RESTRICTED_USER_STATES.CAMERA_AND_MICROPHONE) {
			deviceString = 'camera and microphone';
		} else if (device === RESTRICTED_USER_STATES.CAMERA) {
			deviceString = 'camera';
		} else if (device === RESTRICTED_USER_STATES.MICROPHONE) {
			deviceString = 'microphone';
		}

		const descriptionString = permission
			? `Your browser is preventing access to your ${deviceString}. 
			[Learn how to allow access in your browser.](https://help.brand.live/hc/en-us/articles/4406217830295-Camera-and-Microphone-Access-Required-)`
			: `Greenroom was unable to detect a ${deviceString}. Please check your hardware and try again.`;

		setError({
			message: `${deviceString} access required`,
			description: descriptionString,
		});

		return device;
	};

	const updateMediaStream = async (
		newCam: MediaDeviceInfo | null,
		newMic: MediaDeviceInfo | null,
		restrictedUserState: number,
	) => {
		if (!newCam?.deviceId && !newMic?.deviceId) {
			if (restrictedUserState !== RESTRICTED_USER_STATES.CAMERA_AND_MICROPHONE) {
				getError(RESTRICTED_USER_STATES.CAMERA_AND_MICROPHONE, false);
			}
			return;
		}

		// open the stream once and check liveness directly, avoiding the
		// getLiveDevices open→close→getCameraStream open race on the audio source
		const str = await VideoStreams.getCameraStream(newCam?.deviceId, newMic?.deviceId);
		const hasCam = str.getVideoTracks()[0]?.readyState === 'live';
		const hasMic = str.getAudioTracks()[0]?.readyState === 'live';

		if (hasCam || hasMic) {
			const restrictedDeviceStates = [
				RESTRICTED_USER_STATES.CAMERA,
				RESTRICTED_USER_STATES.MICROPHONE,
				RESTRICTED_USER_STATES.CAMERA_AND_MICROPHONE,
			];

			if (!hasCam && restrictedUserState !== RESTRICTED_USER_STATES.CAMERA) {
				getError(RESTRICTED_USER_STATES.CAMERA, false);
			}

			if (!hasMic && restrictedUserState !== RESTRICTED_USER_STATES.MICROPHONE) {
				getError(RESTRICTED_USER_STATES.MICROPHONE, false);
			}

			if (hasCam && hasMic && restrictedDeviceStates.includes(restrictedUserState)) {
				dispatch(SessionActions.setRestrictedUserState(RESTRICTED_USER_STATES.NONE));
				hideDeviceAlert();
			}

			if (!opentok?.session.isConnected()) {
				dispatch(VideoActions.setMainVideoStream(str));
			} else {
				VideoStreams.deleteStream(str);
			}
		} else {
			VideoStreams.deleteStream(str);
			if (restrictedUserState !== RESTRICTED_USER_STATES.CAMERA_AND_MICROPHONE) {
				getError(RESTRICTED_USER_STATES.CAMERA_AND_MICROPHONE, false);
			}
		}
	};

	// initializes hardware devices and fires if device permissions change
	const checkMediaDevices = async (forceEnabled = false): Promise<MediaCheckResult> => {
		// get current redux state as this may fire from an permissions event
		const {
			VideoReducer: {
				selectedVideo: selectedVideoRedux,
				selectedAudio: selectedAudioRedux,
				selectedOutput: selectedOutputRedux,
				selectedResolution: selectedResolutionRedux,
				availableResolutions: availableResolutionsRedux,
				isInitialDeviceLoad: isInitialDeviceLoadRedux,
			},
			SessionReducer: {
				token: tokenRedux,
				restrictedUserState: restrictedUserStateRedux,
			},
		} = store.getState() as GRS;

		try {
			let cameraPermission = await VideoStreams.getCameraPermissionState(forceEnabled);
			let micPermission = await VideoStreams.getMicPermissionState(forceEnabled);

			if (cameraPermission === 'off' || micPermission === 'off') {
				return 'permissions_off';
			}

			// if we reset permissions, we need to create a new stream for opentok
			if (!isInitialDeviceLoadRedux && (cameraPermission === 'prompt' || micPermission === 'prompt')) {
				dispatch(VideoActions.destroyMainVideoStream());
			}

			//! this is what is causing the green dot in the home view, before you get to a meeting
			// when JS requests getUserMedia it is telling the browser to turn on the camera and mic
			const mediaSupported = !!navigator.mediaDevices?.getUserMedia;
			if (!mediaSupported) {
				console.warn('[checkMediaDevices] navigator.mediaDevices.getUserMedia not available — skipping device check');
				return 'getUserMedia_unavailable';
			}
			let tempStream = null;
			const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;

			{
				let currentRestrictedState = restrictedUserStateRedux;
				if ((window as any).chrome) {
					// if we don't have permissions, request them first, then
					// when we're done getting all the streams, delete the temp stream.
					if (cameraPermission !== 'granted' || micPermission !== 'granted') {
						try {
							tempStream = await VideoStreams.getDefaultCameraStream();
						} catch (e) {
							cameraPermission = await VideoStreams.getCameraPermissionState();
							micPermission = await VideoStreams.getMicPermissionState();
						}
					}

					if (cameraPermission === 'denied' && micPermission === 'denied') {
						currentRestrictedState = getError(RESTRICTED_USER_STATES.CAMERA_AND_MICROPHONE, true);
					} else if (cameraPermission === 'denied') {
						currentRestrictedState = getError(RESTRICTED_USER_STATES.CAMERA, true);
					} else if (micPermission === 'denied') {
						currentRestrictedState = getError(RESTRICTED_USER_STATES.MICROPHONE, true);
					}
				} else if (isFirefox) {
					const allDevices = async () => {
						const devices = await navigator.mediaDevices.enumerateDevices();
						return devices;
					};
					await navigator.mediaDevices.getUserMedia({ video: true, audio: true });

					const devices = await allDevices();

					const noPermissions: any = {};
					devices.forEach((mediaDevice) => {
						noPermissions[mediaDevice.kind] = mediaDevice.label === '';
					});

					if (noPermissions?.videoinput && noPermissions?.audioinput) {
						currentRestrictedState = getError(RESTRICTED_USER_STATES.CAMERA_AND_MICROPHONE, true);
					} else if (noPermissions?.videoinput) {
						currentRestrictedState = getError(RESTRICTED_USER_STATES.CAMERA, true);
					} else if (noPermissions?.audioinput) {
						currentRestrictedState = getError(RESTRICTED_USER_STATES.MICROPHONE, true);
					}
				} else {
					try {
						tempStream = await VideoStreams.getDefaultCameraStream();
					} catch (e) {
						currentRestrictedState = getError(RESTRICTED_USER_STATES.CAMERA_AND_MICROPHONE, true);
					}
				}

				if (tempStream) {
					VideoStreams.deleteStream(tempStream);
				}

				const {
					cameras,
					microphones,
					speakers,
				} = await updateMediaDevices();

				deviceRef.current = { cameras, microphones, speakers };

				const oldCam = localStorage.getItem('camera');
				const oldCamUnsafe = cameras.find((c) => c.deviceId === oldCam);
				const cameraToUse = oldCamUnsafe ?? cameras[0];
				if (cameraToUse && !selectedVideoRedux) {
					dispatch(VideoActions.setSelectedVideo(cameraToUse));
				}

				const oldMic = localStorage.getItem('microphone');
				const oldMicUnsafe = microphones.find((m) => m.deviceId === oldMic);
				const micToUse = oldMicUnsafe ?? microphones[0];
				if (micToUse && !selectedAudioRedux) {
					dispatch(VideoActions.setSelectedAudio(micToUse));
				}

				const oldOutput = localStorage.getItem('speakers');
				const oldOutputUnsafe = speakers.find((s) => s.deviceId === oldOutput);
				const outputToUse = oldOutputUnsafe ?? speakers[0];
				if (outputToUse && !selectedOutputRedux) {
					dispatch(VideoActions.setSelectedAudioOutput(outputToUse));
				}

				if (availableResolutionsRedux[0] && !selectedResolutionRedux) {
					dispatch(VideoActions.setSelectedResolution(availableResolutionsRedux[0]));
				}

				await updateMediaStream(
					cameraToUse,
					micToUse,
					currentRestrictedState,
				);
			}
			return 'ok';
		} catch (e) {
			console.error(e);
			let message = 'Unknown Error';
			if (e instanceof Error) {
				message = e.message;
				ReportError(tokenRedux || '', window.navigator.userAgent, e.stack, message).catch((e2) => {
					console.error(e2);
				});
			}

			dispatch(SessionActions.setRestrictedUserState(RESTRICTED_USER_STATES.CAMERA_AND_MICROPHONE));
			if (e instanceof Error) {
				setError({
					message: 'camera and microphone access required',
					description: `${e.message}.`,
				});
			}
			return `error:${message}`;
		} finally {
			dispatch(VideoActions.setIsInitialDeviceLoad(false));
		}
	};

	const getNewSelectedDevice = (type: DeviceTypes, devices: MediaDeviceInfo[]) => {
		const oldDeviceId = localStorage.getItem(type.toLowerCase());
		let oldDevice = devices.find((d) => d.deviceId === oldDeviceId);
		if (!oldDevice) {
			oldDevice = devices.find((d) => d.deviceId === 'default')
		}
		return oldDevice ?? devices[0] ?? null;
	};

	const isDiff = (n: MediaDeviceInfo | null, o: MediaDeviceInfo | null) => {
		if (!n) return false;
		// "default" device - compare label to detect hardware change
		if (o?.deviceId === 'default') {
			return n.label !== o.label;
		}
		// Specific device - only switch if deviceId changed (i.e., disconnected)
		return n.deviceId !== o?.deviceId;
	}

	const determineNewDevice = (
		currentDevice: MediaDeviceInfo | null,
		availableDevices: MediaDeviceInfo[],
		deviceType: DeviceTypes
	): MediaDeviceInfo | null => {
		// if the current device is not default and still exists in the new list, keep it
		if (currentDevice && currentDevice.deviceId !== 'default' && availableDevices.find((d) => d.deviceId === currentDevice.deviceId)) {
			return currentDevice;
		}
		
		const newDevice = getNewSelectedDevice(deviceType, availableDevices);
		
		// if the old device was specific (not default) and we are changing, it means it was disconnected
		if (currentDevice && currentDevice.deviceId !== 'default' && isDiff(newDevice, currentDevice)) {
			console.log(`Forced ${deviceType} device change due to disconnection`, {
				type: deviceType,
				oldDevice: currentDevice,
				newDevice: newDevice
			});
			showAlertLong({
				message: `${deviceType} Disconnected`,
				description: `Your ${deviceType.toLowerCase()} was disconnected. Switched to ${newDevice?.label || 'default device'}.`,
				type: 'media-device',
			});
		} else if (currentDevice && currentDevice.deviceId !== 'default' && !newDevice) {
			// This handles the case where the last device was disconnected.
			console.log(`Forced ${deviceType} device change due to disconnection. No other device available.`, {
				type: deviceType,
				oldDevice: currentDevice,
				newDevice: null
			});
			showAlertLong({
				message: `${deviceType} Disconnected`,
				description: `Your ${deviceType.toLowerCase()} was disconnected. No other device is available.`,
				type: 'media-device',
			});
		}
		return newDevice;
	};

	// fires when a device is plugged or unplugged
	const onDeviceChange = async () => {
		// get current redux state as this is fired from an event listener
		const {
			VideoReducer: {
				selectedVideo: oCam,
				selectedAudio: oMic,
				selectedOutput: oSpeaker,
				isInitialDeviceLoad: beforeInit,
			},
			SessionReducer: {
				restrictedUserState: rus,
			},
		} = store.getState() as GRS;

		// do not fire until checkMediaDevices has initialized the devices
		if (beforeInit) return;

		const currDevices = await updateMediaDevices();

		const nCam = determineNewDevice(oCam, currDevices.cameras, DeviceTypes.Camera);
		if (nCam && isDiff(nCam, oCam)) changeVideoDevice(nCam);

		const nMic = determineNewDevice(oMic, currDevices.microphones, DeviceTypes.Microphone);
		if (nMic && isDiff(nMic, oMic)) changeAudioDevice(nMic);

		const nSpeaker = determineNewDevice(oSpeaker, currDevices.speakers, DeviceTypes.Speakers);
		if (nSpeaker && isDiff(nSpeaker, oSpeaker)) dispatch(VideoActions.setSelectedAudioOutput(nSpeaker));

		deviceRef.current = currDevices;

		await updateMediaStream(nCam, nMic, rus);
	};

	const updateDeviceRef = async () => {
		deviceRef.current = await updateMediaDevices();
	}

	const permissionListener = async (name: string) => {
		if (!navigator.permissions) return; // for tests
		// @ts-ignore - camera and microphone name prop supported on chrome but not firefox
		const perm = await navigator.permissions.query( { name } );
		perm.onchange = () => {updateDeviceRef()}
	}

	useEffect(() => {
		updateDeviceRef();
		permissionListener('camera');
		permissionListener('microphone');
	}, []);

	return {
		checkMediaDevices,
		onDeviceChange,
		error,
	};
};

export default useMediaDevices;
